
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import IResource from "@/IResource";
import IOrganisationAvailability from "@/IOrganisationAvailability";
import { IAvailabilityValue } from "@/IAvailability";

interface CalendarDay {
		isToday: boolean;
		isCurrentMonth: boolean;
		isPast: boolean;
		isSelected: boolean;
		date: Date;
		dateKey: string;
	}

	@Component
	export default class SingledayCalendar extends Vue {

		@Prop({
			required: true,
			type: Array
		})
		public resources!: IResource[];

		@Prop({
			required: true,
			type: Object
		})
		public availability!: IOrganisationAvailability;

		@Prop({
			default: true,
			type: Boolean
		})
		public showPopup!: boolean;

		public month: number = 0;
		public year: number = 0;

		public selectedDate: Date | null = null;

		public monthNames: string[] = [
			'',
			'Januar',
			'Februar',
			'März',
			'April',
			'Mai',
			'Juni',
			'Juli',
			'August',
			'September',
			'Oktober',
			'November',
			'Dezember'
		];

		public calendarView: CalendarDay[][] = [[]];

		get offset(): number {
			let firstDayOfMonth: Date = new Date(this.year, this.month - 1, 1);
			let weekday = firstDayOfMonth.getDay();
			let offset = weekday - 1;
			if (weekday == 0) {
				offset = 6;
			}
			return offset;
		}

		get firstDayOfCalendar(): Date {
			return new Date(this.year, this.month - 1, 1 - this.offset);
		}

		get firstDayOfMonth(): Date {
			return new Date(this.year, this.month - 1, 1);
		}

		get mintime(): Date {
			// get minimum min_hours_to_reservation from all resources
			let min_date = new Date();
			let min_hours_to_reservation: number = 87600;
			if (this.resources) {
				this.resources.forEach((resource: IResource) => {
					if (resource.min_hours_to_reservation < min_hours_to_reservation) {
						min_hours_to_reservation = resource.min_hours_to_reservation;
					}
				})
			}
			min_date.setHours(min_date.getHours() + min_hours_to_reservation);
			return min_date;
		}

		get maxtime(): Date {
			// get minimum max_hours_to_reservation from all resources
			let max_date = new Date();
			let max_hours_to_reservation: number = 0;
			if (this.resources) {
				this.resources.forEach((resource: IResource) => {
					if (resource.max_hours_to_reservation > 0 && resource.max_hours_to_reservation > max_hours_to_reservation) {
						max_hours_to_reservation = resource.max_hours_to_reservation;
					} else if (resource.max_hours_to_reservation === 0) {
						max_hours_to_reservation = 87600;
					}
				})
			}
			max_date.setHours(max_date.getHours() + max_hours_to_reservation);
			return max_date;
		}

		get starttime(): number {
			// get earliest starttime from all resources
			let starttime: number = 24;
			if (this.resources) {
				this.resources.forEach((resource: IResource) => {
					if (resource.starttime < starttime) {
						starttime = resource.starttime;
					}
				})
			}
			return starttime;
		}

		get endtime(): number {
			// get latest endtime from all resources
			let endtime: number = 0;
			if (this.resources) {
				this.resources.forEach((resource: IResource) => {
					if (resource.endtime > endtime) {
						endtime = resource.endtime;
					}
				})
			}
			return endtime;
		}

		get canGoPreviousMonth() {
			let today = new Date();
			return this.year == today.getFullYear() && this.month > today.getMonth() + 1 || this.year > today.getFullYear();
		}

		get colorBooked() {
			try {
				let cssVariable = getComputedStyle(document.documentElement).getPropertyValue('--singleday-calendar-bg-color--booked');
				return cssVariable ? cssVariable.trim().substr(0,7) : '#ffcba6';
			} catch (e) {
				return '#ffcba6';
			}
		}

		get colorNew() {
			try {
				let cssVariable = getComputedStyle(document.documentElement).getPropertyValue('--singleday-calendar-bg-color--new');
				return cssVariable ? cssVariable.trim().substr(0,7) : '#ffcba6';
			} catch (e) {
				return '#ffcba6';
			}
		}

		get colorLocked() {
			try {
				let cssVariable = getComputedStyle(document.documentElement).getPropertyValue('--singleday-calendar-bg-color--locked');
				return cssVariable ? cssVariable.trim().substr(0,7) : '#ffcba6';
			} catch (e) {
				return '#ffcba6';
			}
		}

		public mounted() {
			let today = new Date();
			this.year = today.getFullYear();
			this.month = today.getMonth() + 1;

			this.$gtag.pageview( {page_path: '/singleday-calendar' });
		}

		@Watch('mintime')
		@Watch('maxtime')
		@Watch('month')
		@Watch('selectedDate')
		public watchMonth() {
			this.buildCalendarView();
		}

		public hasReservationsPopover(day: CalendarDay): boolean {
			if (this.resources.every(resource => !resource.reservation_show_details)) {
				return false;
			}
			return this.resources.some(resource => {
				return this.availability[resource.id] && this.availability[resource.id][day.dateKey];
			})
		}

		public reservationsPopoverAvailability(day: CalendarDay): IAvailabilityValue[] {
			let availabilities: IAvailabilityValue[] = [];
			this.resources.forEach(resource => {
				let availability = this.availability[resource.id];
				if (availability && availability[day.dateKey]) {
					availability[day.dateKey].forEach(availabilityValue => {
						// only show resource name for multi-resource calendar
						if (availabilityValue[2] !== null && this.resources.length > 1) {
							availabilityValue[2].resourcename = resource.name;
						}
						availabilities.push(availabilityValue);
					})
				}
			});
			availabilities = availabilities.sort((a, b) => a[0] - b[0]);
			return availabilities;
		}

		public formatTime(minutes: number): string {
			let mins: number|string = minutes % 60;
			if (mins < 10) {
				mins = "0" + mins;
			}
			let hours: number|string = Math.floor(minutes / 60);
			if (hours < 10) {
				hours = "0" + hours;
			}
			return hours + ':' + mins;
		}

		public bookedStyle(day: CalendarDay): { backgroundColor?: string; backgroundImage?: string, backgroundSize?: 'contain', backgroundRepeat?: 'no-repeat' } {
			let col: number = 0;
			let cols: number = this.resources.length;
			let col_width: number = 100 / cols;
			let rects: string[] = [];
			this.resources.forEach(resource => {
				let availability = this.availability[resource.id];
				if (availability && availability[day.dateKey]) {
					const startminute = this.starttime * 60;
					const endminute = this.endtime * 60;
					rects.push(availability[day.dateKey]
						.map((slot): IAvailabilityValue => [(slot[0] - startminute) / (endminute - startminute), (slot[1] - startminute) / (endminute - startminute), slot[2] ? slot[2] : null])
						.sort((a, b) => {
							// paint locktimes in the background
							if (!a[2] || !b[2]) return 0;
							if (a[2].status === 'locked') return -1;
							if (b[2].status === 'locked') return 1;
							return 0;
						})
						.map(slot => {
							let fillcolor = this.colorBooked;
							let strokecolor = this.colorBooked;
							let details = slot[2];
							if (details && details.status && details.status === 'locked') {
								fillcolor = this.colorLocked;
								strokecolor = this.colorLocked;
							} else if (details && details.status && details.status === 'new') {
								fillcolor = 'url(#diagonalHatch)';
								strokecolor = this.colorNew;
							}
							return `<rect x='${col * col_width}%' y='${Math.round(100 * slot[0])}%' width='${col_width}%' height='${Math.round(100 * (slot[1] - slot[0]))}%' fill='${fillcolor}' stroke='${strokecolor}'/>`;
						})
						.join('')
					);
				}
				col++;
			})

			if (rects.length > 0) {
				// detect ie11 and edge
				// @ts-ignore
				if (!!window.StyleMedia) {
					// old browsers: if there is any availability, just change the color of the day
					return {
						backgroundColor: this.colorBooked,
						backgroundSize: 'contain'
					};
				} else {
					let pattern = `<defs><pattern id='diagonalHatch' width='10' height='10' patternTransform='rotate(45 0 0)' patternUnits='userSpaceOnUse'><rect x="0" y="0" width="10" height="10" style="fill:${this.colorNew}"/><line x1='0' y1='0' x2='0' y2='10' style='stroke:#ffffff66; stroke-width:10'/></pattern></defs>`
					let svg = rects.join('');
					svg = `<svg xmlns='http://www.w3.org/2000/svg'>${pattern}${svg}</svg>`;
					return {
						backgroundImage: `url("data:image/svg+xml;base64,${btoa(svg)}")`,
						backgroundSize: 'contain',
						backgroundRepeat: 'no-repeat'
					}
				}
			} else {
				return {};
			}
		}

		public previousMonth() {
			if(this.canGoPreviousMonth) {
				this.month--;
				if (this.month <= 0) {
					this.month = 12;
					this.year--;
				}
			}
		}

		public nextMonth() {
			this.month++;
			if (this.month > 12) {
				this.month = 1;
				this.year++;
			}
		}

		public selectDate(day: CalendarDay) {
			if (!day.isPast) {
				this.selectedDate = new Date(day.date);
				this.$emit('selectdate', day.date)
			}
		}

		public buildCalendarView() {
			let view: CalendarDay[][] = [];
			let row;
			for (row = 0; row < 6; row++) {
				view[row] = [];
				let col;
				for (col = 0; col < 7; col++) {
					let calendarDayDate = new Date(this.year, this.month - 1, 1 - this.offset + (col + 7 * row));
					let today = new Date();
					let isToday = false;
					if (today.getFullYear() == calendarDayDate.getFullYear() && today.getMonth() == calendarDayDate.getMonth() && today.getDate() == calendarDayDate.getDate()) {
						isToday = true;
					}
					let firstDay = new Date(this.mintime.getFullYear(), this.mintime.getMonth(), this.mintime.getDate());
					let lastDay = new Date(this.maxtime.getFullYear(), this.maxtime.getMonth(), this.maxtime.getDate());
					let isPast = calendarDayDate < firstDay || calendarDayDate > lastDay;
					let isSelected = this.selectedDate !== null && this.selectedDate.getFullYear() == calendarDayDate.getFullYear() && this.selectedDate.getMonth() == calendarDayDate.getMonth() && this.selectedDate.getDate() == calendarDayDate.getDate();
					let isCurrentMonth = false;
					if (this.firstDayOfMonth.getFullYear() == calendarDayDate.getFullYear() && this.firstDayOfMonth.getMonth() == calendarDayDate.getMonth()) {
						isCurrentMonth = true;
					}
					view[row][col] = {
						isToday: isToday,
						isCurrentMonth: isCurrentMonth,
						date: calendarDayDate,
						isPast: isPast,
						isSelected: isSelected,
						dateKey: calendarDayDate.getFullYear() + '-' + (calendarDayDate.getMonth() + 1) + '-' + calendarDayDate.getDate()
					}
				}
			}
			this.calendarView = view;
		}
	}
